import {
	Outlet,
	Navigate,
	type RouteObject,
	useLocation,
	useParams,
} from "react-router-dom";
import { PageContainter, PageWithBackground } from "./SubPageLayout";
import { PlayerAuth } from "./PlayerAuth";
import { PlayerAuthRoute } from "./pages/PlayerAuthRoute";
import { VideoAndTimeoutWrapper } from "./components/VideoAndTimeoutWrapper";
import { BigButtonsPage } from "./components/BigButtonsPage";
import { TvMuralButton } from "./components/TvMuralButton";
import { BigCategoriesList } from "./components/BigCategoriesList";

import { Header } from "./components/Header";
import EmbedPage from "./pages/EmbedPage";

import { BUTTONS } from "./buttons";
import Logo from "./components/Logo";
import { ProvideReactQuery } from "./config/reactQuery";
import { UcOnlineButton } from "./components/UcOnlineButton";
import { MotionMain } from "./MotionMain";
import { CategoryRender } from "./pages/CategoryRender";
import { usePlayerData } from "./usePlayerData";
import { AnalyticsProvider } from "./hooks/useAnalytics";
import { MegaMidiaTiagoAnalytics } from "./analytics/megamidiaTiago";
import { useQuery } from "@tanstack/react-query";
import { IconLoader, IconNetwork, IconNetworkOff } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { FirebaseAuthProvider } from "../dashboard/firebase";
import { GroupVideos } from "./pages/GroupVideos";
import { AoVivoPage } from "./pages/AoVivoPage";

import { ICheckStrategy } from "./connection-check/IConnectionCheck";
import { FirebaseDisconnectCheck } from "./connection-check/firebase-connection-check";
import { UNIVERSIDADE_CORPORATIVA_CATEGORIA_ID } from "./constants";

declare global {
	interface Window {
		api?: {
			openExternalLink: (url: string) => void;
			isInstalledPlayer: boolean;
			onRequestPassword: (callback: () => void) => void;
			offRequestPassword?: (callback: () => void) => void;
			passwordAccepted: () => void;
			requestExit: () => void;
		};
		getVideoFromDb: (url: string) => Promise<any>;
		loginWithEmailAndPassword: (
			email: string,
			password: string,
		) => Promise<any>;
	}
}


const analytics = new MegaMidiaTiagoAnalytics();
const playerStatusCheck = new FirebaseDisconnectCheck();

export const PlayerData = ({ children }: { children: React.ReactNode }) => {
	const playerData = usePlayerData();

	return (
		<div data-player={playerData.password} className="h-screen flex flex-col">
			{children}
		</div>
	);
};

const getIcon = (status: "ok" | "error" | "warn", loading: boolean) => {
	if (loading) {
		return IconLoader;
	}

	if (status === "ok") {
		return IconNetwork;
	}

	if (status === "error") {
		return IconNetworkOff;
	}

	return null;
};

export const StatusBar = ({
	status,
	message,
	loading = false,
}: { status: "ok" | "error" | "warn"; message: string; loading?: boolean }) => {
	const color =
		status === "ok" ? "green" : status === "error" ? "red" : "yellow";

	const Icon = getIcon(status, loading);

	return (
		<div
			className={`fixed bottom-10 right-0 bg-${color}-500 text-white p-2 z-50 flex items-center space-x-2 left-0 justify-center`}
		>
			{Icon && (
				<div>
					<Icon size={30} />
				</div>
			)}
			<div>{message}</div>
		</div>
	);
};

export const PlayerStatus = ({
	check,
	children,
}: { check: ICheckStrategy; children: React.ReactNode }) => {
	const [visible, setVisible] = useState(false);

	const checkQuery = useQuery({
		queryKey: ["playerStatus"],
		queryFn: () => check.check(),
		cacheTime: 0,
		enabled: true,
		networkMode: "always",
		refetchInterval: 1000,
		staleTime: 0,
	});

	useEffect(() => {
		setVisible(true);
		const timeout = setTimeout(() => {
			if (checkQuery.data?.status === "ok") {
				setVisible(false);
			}
		}, 5000);

		return () => {
			setVisible(false);
			clearTimeout(timeout);
		};
	}, [checkQuery.data?.status]);

	let statusBar: React.ReactNode = null;

	if (!checkQuery.data && checkQuery.isLoading) {
		statusBar = (
			<StatusBar
				status="warn"
				message={"Verificando conexão com a internet"}
				loading
			/>
		);
	}

	if (checkQuery.data?.status === "error") {
		statusBar = (
			<StatusBar
				status="error"
				message={checkQuery.data.message || "Instabilidade"}
			/>
		);
	}

	if (checkQuery.data?.status === "ok") {
		statusBar = (
			<StatusBar status="ok" message={checkQuery.data.message || "Ok"} />
		);
	}

	return (
		<>
			{children}
			{visible && statusBar}
		</>
	);
};


const UNIVERSIDADE_CORPORATIVA_GROUP_ID = "poNW6e8JgD9mCrOFDBxX";
const UNIVERSIDADE_CORPORATIVA_GROUP_NAME = "universidade_corporativa";
const CONDOR_NEWS_GROUP_ID = "GlwEMPb43Ee6hkWCxYIT";
const INSTITUTO_JOANIR_ZONTA_GROUP_ID = "VQyoztFlB4zWXBhPLmPQ";
const RECRUTAMENTO_E_SELECAO_GROUP_ID = "JF71FgD8ekhS5zqc8YNP";

const TrainingCategoryRoute = () => {
	const params = useParams();
	const categoryId = params.category || UNIVERSIDADE_CORPORATIVA_CATEGORIA_ID;
	const subCategoryPathIds = (params["*"] || "")
		.split("/")
		.map((part) => part.trim())
		.map((part) => part.replace(/^sc:/, "sc__"))
		.filter(Boolean);

	return (
		<CategoryRender
			groupFilter={{ 
				groupId: UNIVERSIDADE_CORPORATIVA_GROUP_ID, 
				groupName: UNIVERSIDADE_CORPORATIVA_GROUP_NAME,
			}}
			categoryId={categoryId}
			subCategoryPathIds={subCategoryPathIds}
			name="Universidade Corporativa"
			suffixButton={<UcOnlineButton />}
		/>
	);
};

export const routes: RouteObject = {
	path: "/",
	element: (
		<FirebaseAuthProvider>
			<ProvideReactQuery>
				<PlayerStatus check={playerStatusCheck}>
					<AnalyticsProvider analytics={analytics}>
						<PageWithBackground>
							<Outlet />
						</PageWithBackground>
					</AnalyticsProvider>
				</PlayerStatus>
			</ProvideReactQuery>
		</FirebaseAuthProvider>
	),
	children: [
		{
			path: "",
			element: <PlayerAuth />,
		},
		{
			path: "player",
			Component: () => {
				const location = useLocation();
				const disableHeader = /\/player$/.test(location.pathname);
				const headerIsVisible = !disableHeader;
				const isTransmissaoAoVivo = location.pathname.includes("transmissaoaovivo");
				return (
					<VideoAndTimeoutWrapper>
						<PlayerAuthRoute>
							<PlayerData>
								{headerIsVisible && <Header />}
								<PageContainter disabled={disableHeader || isTransmissaoAoVivo}>
									<Outlet />
								</PageContainter>
							</PlayerData>
						</PlayerAuthRoute>
					</VideoAndTimeoutWrapper>
				);
			},
			children: [
				{
					path: "",
					element: (
						<MotionMain key="/" className="flex flex-col md:h-full">
							<BigButtonsPage
								prefix={<Logo className="mt-8 md:mt-0 w-[30%]" />}
								buttons={[
									BUTTONS.INSTITUTO_JOANIR_ZONTA,
									BUTTONS.ZONTA_NEWS,
									BUTTONS.UNIVERSIDADE_CORPORATIVA,
									BUTTONS.RECRUTAMENTO_E_SELECAO,
									BUTTONS.AO_VIVO,
								]}
								suffix={<TvMuralButton />}
							/>
						</MotionMain>
					),
				},
				{
					path: "condor_news",
					element: (
						<MotionMain key="/condor_news" className="flex flex-col h-full">
							<Outlet />
						</MotionMain>
					),
					children: [
						{
							path: "",
							element: (
								<MotionMain
									key="/condor_news/index"
									className="flex flex-col h-full"
								>
									<BigCategoriesList
										groupFilter={{ groupId: CONDOR_NEWS_GROUP_ID, groupName: "condor_news" }}
										name="Zonta News"
									/>
								</MotionMain>
							),
						},
						{
							path: ":category",
							element: (
								<MotionMain key="/condor_news/category">
									<GroupVideos groupFilter={{ groupId: CONDOR_NEWS_GROUP_ID, groupName: "condor_news" }} title="Zonta News" />
								</MotionMain>
							),
						},
						{
							path: ":category/*",
							element: (
								<MotionMain key="/condor_news/subcategory">
									<GroupVideos groupFilter={{ groupId: CONDOR_NEWS_GROUP_ID, groupName: "condor_news" }} title="Zonta News" />
								</MotionMain>
							),
						},
					],
				},
				{
					path: "instituto_joanir_zonta",
					element: (
						<MotionMain
							key="/instituto_joanir_zonta"
							className="flex flex-col h-full"
						>
							<Outlet />
						</MotionMain>
					),
					children: [
						{
							path: "",
							element: (
								<MotionMain
									key="/instituto_joanir_zonta/index"
									className="flex flex-col h-full"
								>
									<GroupVideos
										groupFilter={{ groupId: INSTITUTO_JOANIR_ZONTA_GROUP_ID, groupName: "instituto_joanir_zonta" }}
										title="Instituto Joanir Zonta"
										ignoreCategories
									/>
								</MotionMain>
							),
						},
					],
				},
				{
					path: "recrutamento_e_selecao",
					element: (
						<MotionMain
							key="/recrutamento_e_selecao"
							className="flex flex-col h-full"
						>
							<Outlet />
						</MotionMain>
					),
					children: [
						{
							path: "",
							element: (
								<MotionMain
									key="/recrutamento_e_selecao/index"
									className="flex flex-col h-full"
								>
									<GroupVideos
										groupFilter={{ groupId: RECRUTAMENTO_E_SELECAO_GROUP_ID, groupName: "recrutamento_e_selecao" }}
										title="Recrutamento e Seleção"
										ignoreCategories
									/>
								</MotionMain>
							),
						},
					],
				},
				{
					path: "aovivo",
					element: (
						<MotionMain key="/aovivo" className="flex flex-col h-full">
							<Outlet />
						</MotionMain>
					),
					children: [
						{
							path: "",
							element: <AoVivoPage />,
						},
						{
							path: "transmissaoaovivo",
							element: <EmbedPage name="Live" />,
						},
					],
				},
				{
					path: "treinamento",
					element: (
						<MotionMain key="/treinamento" className="flex flex-col h-full">
							<Outlet />
						</MotionMain>
					),
					children: [
						{
							path: "",
							element: (
								<Navigate
									to={UNIVERSIDADE_CORPORATIVA_CATEGORIA_ID}
									replace
								/>
							),
						},
						{
							path: "uconline",
							element: <EmbedPage name="UC Online" />,
						},
						{
							path: ":category",
							Component: TrainingCategoryRoute,
						},
						{
							path: ":category/*",
							Component: TrainingCategoryRoute,
						},
					],
				},
			],
		},
	],
};
